/**
 * Sheet 13, the run while it runs (mission 6 WP07 T041; FR-011, FR-027,
 * NFR-005; GEN.4, GEN.5, GEN.7, NF.2, D.8, B.3; research §1.4, §1.17;
 * post-plan C10, C13, T17; post-tasks P9).
 *
 * The dispatcher sends queued, validating, generating, analyzing, failed,
 * cancelled and any unknown state here with the polled `run`; this screen
 * never polls or navigates. At `ready` the dispatcher renders Results at the
 * same URL (13/1).
 * - Queued: register 7's queued sentence with no queue position (13/8, 13/9;
 *   NF.2), then the stepper and the stage log.
 * - Validating, generating, analyzing and unknown: the stepper and the stage log.
 * - Failed: the stepper only where the reason names a stage, then the reason
 *   in words, "nothing was consumed" and the full run id as the support
 *   reference; no retry (13/10, C-013).
 * - Cancelled: Cancelled · N credits from `credits_consumed`, and nothing
 *   consumed (13/2, D.8).
 * Cancel sits in the header until the run is terminal.
 *
 * Mission 8 WP10 (T052; U21.1; research §1.20): a URL carrying `?assistant`
 * loses it here with a replace (other params and the hash kept, the router
 * state dropped: mission 8 review UI5), so the panel never opens by itself
 * when the run turns ready. No history entry is added.
 */
import { useEffect, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Band } from '@/components/trellis/Band'
import { RunHeader } from '@/components/trellis/RunHeader'
import type { Run } from '@/lib/api/runs'
import { withoutAssistantParam } from '@/lib/assistant-panel'
import { Mono, count } from '@/lib/format'
import { PROVISIONAL_COPY, copyText, type TextKey } from '@/lib/provisional-copy'
import { CancelRun } from './progress/CancelRun'
import { RunStepper } from './progress/RunStepper'
import { StageLog } from './progress/StageLog'
import type { RunScreenProps } from './types'

const FAILURE: Record<string, TextKey> = {
  parked: 'failure.parked',
  'upload-object-changed': 'failure.uploadObjectChanged',
  'generation-empty': 'failure.generationEmpty',
  'analysis-timeout': 'failure.analysisTimeout',
}

function failureKey(reason: Run['failure_reason']): TextKey {
  return (reason !== undefined && FAILURE[reason]) || 'failure.unknown'
}

/** Renders `text` with the first occurrence of `token` wrapped by `wrap` (a register string around a mono value). */
function around(text: string, token: string, wrap: (t: string) => ReactNode): ReactNode {
  const at = text.indexOf(token)
  if (at < 0) return text
  return (
    <>
      {text.slice(0, at)}
      {wrap(token)}
      {text.slice(at + token.length)}
    </>
  )
}

function Failed({ run }: { run: Run }) {
  const reference = PROVISIONAL_COPY.supportReference.render({ runId: run.run_id })
  return (
    <Band kind="blocked">
      <p>{copyText(failureKey(run.failure_reason))}</p>
      <p>{copyText('nothingConsumed')}</p>
      <p className="mt-1 text-body-sm text-navy-700">{around(reference, run.run_id, (id) => <Mono>{id}</Mono>)}</p>
    </Band>
  )
}

function Cancelled({ run }: { run: Run }) {
  return (
    <Band kind="info">
      {/* "Cancelled · 0 credits" is the drawn label of sheet 13 callout 2. */}
      <p>
        Cancelled · <Mono>{count(run.credits_consumed)}</Mono> credits
      </p>
      <p>{copyText('nothingConsumed')}</p>
    </Band>
  )
}

/**
 * U21.1: drop `assistant` from the URL, replacing the entry, and the router state with it (mission 8 review
 * UI5), so a typed prefill (`location.state.assistant`) cannot survive a reload until Results mounts.
 */
function useStripAssistantParam() {
  const location = useLocation()
  const navigate = useNavigate()
  const present = new URLSearchParams(location.search).has('assistant')
  useEffect(() => {
    if (!present) return
    navigate(
      { pathname: location.pathname, search: withoutAssistantParam(location.search), hash: location.hash },
      { replace: true, state: null },
    )
  }, [present, location.pathname, location.search, location.hash, navigate])
}

export default function InProgress({ run, onRunChanged }: RunScreenProps) {
  useStripAssistantParam()
  let body: ReactNode
  if (run.state === 'failed') {
    body = (
      <>
        <RunStepper run={run} />
        <Failed run={run} />
      </>
    )
  } else if (run.state === 'cancelled') {
    body = <Cancelled run={run} />
  } else {
    body = (
      <>
        {run.state === 'queued' ? (
          <Band kind="info">
            <p>{copyText('queuedSentence')}</p>
          </Band>
        ) : null}
        <RunStepper run={run} />
        <StageLog run={run} />
      </>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <RunHeader run={run} actions={<CancelRun run={run} onRunChanged={onRunChanged} />} />
      <p className="text-caption text-navy-600">{copyText('timesInUtc')}</p>
      {body}
    </div>
  )
}
